/**
 * Working hours for a therapist.
 *
 * The same model the Israeli system drives through the agent API: a standing
 * weekly pattern plus dated exceptions that REPLACE the pattern for their
 * day. Blocks carry their own id so an edit updates the row rather than
 * replacing it, which is what keeps a slot's history attached to the slot.
 */
import api from './api';

export interface AvailabilityBlock {
  id?: number;
  start_time: string;        // "09:00" or "09:00:00"
  end_time: string;
  comment?: string | null;
  meeting_type_id?: number | null;
}

export interface WeeklyBlock extends AvailabilityBlock {
  weekday: number;           // 0 = Sunday .. 6 = Saturday
  valid_from?: string | null;
  valid_to?: string | null;
}

/** A block as it lands on one day. `source` says where it came from, so the
 *  UI can offer "change every Tuesday" and "change this Tuesday" as
 *  different things.
 *
 *  Two of the four are not working hours at all, and they are not the same
 *  as each other. Both arrive in this list because the calendar has to draw
 *  them, so anything reading it as "when can a patient come" has to take
 *  both out:
 *
 *    'cancelled'  an occurrence of the pattern DELETED from this date. The
 *                 hours are not there. Restoring it gives the occurrence
 *                 back to the pattern.
 *    'blocked'    hours KEPT but closed - inside a band that is still
 *                 listed, over hours set for this date alone, or over a
 *                 whole occurrence. Unblocking lifts the refusal and the
 *                 hours stay where they were. */
export interface ResolvedBlock extends AvailabilityBlock {
  source: 'weekly' | 'date' | 'cancelled' | 'blocked' | string;
}

export interface ResolvedDay {
  date: string;
  blocks: ResolvedBlock[];
}

/** Total time the therapist is OFFERING in the resolved days, as HH:MM.
 *
 *  The availability itself - what the therapist has put up - not what is
 *  left of it, and so blocked slots are in it as much as active ones.
 *  Meetings already booked into those hours are not subtracted:
 *  resolve() on the server never looks at them, and the question this
 *  answers is how much the therapist is giving, not how much is still free.
 *
 *  Summed from `days`, which is what the period actually RESOLVES to -
 *  the weekly pattern already expanded, dated overrides applied and
 *  cancelled occurrences removed. Summing the weekly pattern instead would
 *  count hours the therapist has cancelled.
 *
 *  Minutes, not decimal hours: 7.5 reads as seven and a half to some people
 *  and seven hours fifty to others, and these are shift lengths.
 */
export const availableHHMM = (days?: ResolvedDay[] | null): string => {
  const minutes = (days || []).reduce((total, day) => total
    // The two refusals count differently, because they are different things.
    //
    // Blocked time comes OFF: it sits inside a band that is itself in this
    // list and counted in full, so ignoring it would count hours the
    // therapist has closed. A cancelled occurrence counts as NOTHING: the
    // hours were deleted, so no band for them is in the list to correct -
    // subtracting it would take the same hours off twice.
    // A BLOCKED slot counts. The question this answers is how much the
    // therapist has put up, and a blocked slot is hours they put up and
    // closed for now - it is still part of their week, and leaving it out
    // made the total drop every time somebody held a morning for a meeting.
    // A CANCELLED one does not: those hours were deleted from the date and
    // are not there at all.
    + (day.blocks || []).reduce((d, b) => b.source === 'cancelled' ? d
        : d + blockMinutes(b.start_time, b.end_time), 0), 0);
  const offered = Math.max(0, minutes);
  return `${Math.floor(offered / 60)}:${String(offered % 60).padStart(2, '0')}`;
};

/** Minutes between two "HH:MM[:SS]" wall-clock times on one day. */
const blockMinutes = (start: string, end: string): number => {
  const mins = (t: string) => {
    const [h, m] = (t || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
  };
  const a = mins(start); const b = mins(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  // A block that ends before it starts is bad data, not an overnight shift:
  // these are clinic hours. Counting it negative would hide real time.
  return Math.max(0, b - a);
};

export interface AvailabilityResponse {
  user_id: number;
  /** Whether the signed-in user may change these hours. The server decides;
   *  the UI must not infer it from a role, because in this tenant everybody
   *  is an admin. */
  editable: boolean;
  days: ResolvedDay[];
  weekly: WeeklyBlock[];
}

export interface AvailabilityChange {
  at: string;
  action: string;
  by: string;
  scope?: string | null;
  before?: string | null;
  after?: string | null;
  description?: string | null;
}

export const availabilityService = {
  async get(fromDate: string, toDate: string, userId?: number) {
    const { data } = await api.get<AvailabilityResponse>('/availability/', {
      params: { from_date: fromDate, to_date: toDate, ...(userId ? { user_id: userId } : {}) },
    });
    return data;
  },

  /** Replace the standing pattern for one weekday. An empty list clears it. */
  async setWeekly(weekday: number, blocks: WeeklyBlock[], userId?: number) {
    const { data } = await api.put('/availability/weekly',
      { weekday, blocks },
      { params: userId ? { user_id: userId } : {} });
    return data;
  },

  /** Set one date's hours, replacing the pattern for that date alone. An
   *  empty list means "not working", which is different from having no
   *  override at all. */
  async setOverride(onDate: string, blocks: AvailabilityBlock[], userId?: number) {
    const { data } = await api.put('/availability/override',
      { on_date: onDate, blocks },
      { params: userId ? { user_id: userId } : {} });
    return data;
  },

  /** Turn ONE slot blocked, or turn it back on.
   *
   *  A date holds slots that do not overlap, each active or blocked, so
   *  this names one by its times and moves it between the two. Blocking
   *  takes the whole slot - the server refuses part of one - and `blocked:
   *  false` turns it back on, leaving an active slot behind rather than
   *  nothing.
   */
  async setBlock(onDate: string, startTime: string, endTime: string,
                 blocked: boolean, comment?: string | null, userId?: number) {
    const { data } = await api.put('/availability/blocked',
      { on_date: onDate, start_time: startTime, end_time: endTime,
        blocked, comment: comment ?? null },
      { params: userId ? { user_id: userId } : {} });
    return data;
  },

  /** Who changed a slot, when, and what changed. `availabilityId` gives one
   *  slot's own history; without it the answer is the therapist's whole week,
   *  which in front of a single block reads as unrelated noise. */
  async history(opts: {
    userId?: number; availabilityId?: number; onDate?: string;
    weekday?: number; limit?: number;
  }) {
    const { data } = await api.get<{ changes: AvailabilityChange[] }>(
      '/availability/history', {
        params: {
          ...(opts.userId ? { user_id: opts.userId } : {}),
          ...(opts.availabilityId ? { availability_id: opts.availabilityId } : {}),
          ...(opts.onDate ? { on_date: opts.onDate } : {}),
          ...(opts.weekday !== undefined ? { weekday: opts.weekday } : {}),
          limit: opts.limit ?? 20,
        },
      });
    return data.changes ?? [];
  },
};
